import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import API, { formatApiError } from "@/lib/api";
import { ArrowsClockwise, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { NETWORK_LABELS, NETWORK_BADGE } from "../pub_brands/constants";

/**
 * Publisher → API & Integrations → Postback Logs
 *
 * Self-service delivery audit: every outbound postback we fired to this
 * publisher's URL, with response status + timestamp. Pubs can refire any
 * row whose last response was a failure (4xx / 5xx / ERROR).
 *
 * Refire is rate-limited server-side (1 per row per 60s) — we surface that
 * via the toast on a 429.
 */

export function PubPostbackLogsTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [network, setNetwork] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  // Only networks this publisher actually has brands assigned on — never
  // a static "all 15 networks" list. A pub with no MaxBounty assignment
  // should never see a MaxBounty filter chip.
  const [availableNetworks, setAvailableNetworks] = useState([]);
  // Track in-flight refire per row so we disable the button while it's running.
  const [refiring, setRefiring] = useState(new Set());

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (network !== "all") params.network = network;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await API.get("/publisher/postback-logs", { params });
      setLogs(data.data || []);
      setAvailableNetworks(data.networks || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to load postback logs");
    } finally {
      setLoading(false);
    }
  }, [network, statusFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const refire = async (row) => {
    const key = row.action_id;
    if (refiring.has(key)) return;
    setRefiring((s) => new Set(s).add(key));
    try {
      const { data } = await API.post("/publisher/postback-logs/refire", {
        action_id: row.action_id,
      });
      const newStatus = data?.log?.response_status;
      if (newStatus >= 200 && newStatus < 300) {
        toast.success(`Refire delivered (${newStatus})`);
      } else {
        toast.warning(`Refire fired but server returned ${newStatus}`);
      }
      // Replace just this row in-place so the user sees the new status
      // without losing scroll position.
      setLogs((prev) =>
        data?.log
          ? [data.log, ...prev.filter((r) => !(r.action_id === row.action_id && r.sent_at === row.sent_at))]
          : prev,
      );
    } catch (err) {
      toast.error(formatApiError(err) || "Refire failed");
    } finally {
      setRefiring((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  };

  return (
    <div className="space-y-4" data-testid="pub-postback-logs-tab">
      <div className="border border-blue-200 bg-blue-50/50 rounded-md p-3 flex gap-2">
        <Info size={16} className="text-[#002FA7] shrink-0 mt-0.5" />
        <div className="text-xs text-slate-700">
          Delivery audit for every postback we sent to your URL.
          If your server returned 4xx / 5xx / timed out, hit{" "}
          <strong>Refire</strong> to retry. Refires are limited to 1 per
          conversion every 60 seconds.
        </div>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm font-semibold text-slate-900">
            {logs.length} delivery event{logs.length === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { k: "all", label: "All networks" },
              ...availableNetworks.map((n) => ({
                k: n, label: NETWORK_LABELS[n] || n,
              })),
            ].map((n) => (
              <button
                key={n.k}
                type="button"
                onClick={() => setNetwork(n.k)}
                data-testid={`pub-pb-net-${n.k}`}
                className={`h-7 px-2.5 text-[11px] font-semibold rounded-full border transition-colors ${
                  network === n.k
                    ? "bg-[#002FA7] text-white border-[#002FA7]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
              >
                {n.label}
              </button>
            ))}
            <span className="w-px h-5 bg-slate-200 mx-1" />
            {[
              { k: "all", label: "All" },
              { k: "success", label: "Success" },
              { k: "failed", label: "Failed" },
            ].map((s) => (
              <button
                key={s.k}
                type="button"
                onClick={() => setStatusFilter(s.k)}
                data-testid={`pub-pb-status-${s.k}`}
                className={`h-7 px-2.5 text-[11px] font-semibold rounded-full border transition-colors ${
                  statusFilter === s.k
                    ? s.k === "failed"
                      ? "bg-red-600 text-white border-red-600"
                      : s.k === "success"
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-[#002FA7] text-white border-[#002FA7]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
              >
                {s.label}
              </button>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={fetchLogs}
              disabled={loading}
              className="h-7 text-[11px]"
              data-testid="pub-pb-refresh"
            >
              {loading ? "Loading…" : "Refresh"}
            </Button>
          </div>
        </div>

        {logs.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-slate-400">
            {loading ? "Loading…" : "No postback deliveries yet."}
          </div>
        ) : (
          <div className="max-h-[600px] overflow-auto">
            <table className="w-full text-[12px]" data-testid="pub-pb-table">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="border-b border-slate-200">
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Time</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Network</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Action ID</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Conv. Status</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Your Response</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Fired URL</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold"></th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l, i) => {
                  const rs = l.response_status;
                  const isErr = rs === "ERROR" || (typeof rs === "number" && rs >= 400);
                  const isOk = typeof rs === "number" && rs >= 200 && rs < 300;
                  const respCls = isOk
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : isErr
                    ? "bg-red-50 text-red-700 border-red-200"
                    : "bg-slate-100 text-slate-600 border-slate-200";
                  // "inmobi" is the legacy outbound tag for Shopnomix Kevin
                  // (kept deliberately — see services/inmobi_postback.py)
                  // so normalise it here purely for display.
                  const netKey = l.network === "inmobi" ? "shopnomix_kevin" : l.network;
                  const netCls = NETWORK_BADGE[netKey] || "bg-slate-100 text-slate-600 border-slate-200";
                  const netLabel = NETWORK_LABELS[netKey] || l.network;
                  const isRefiring = refiring.has(l.action_id);
                  return (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`pub-pb-row-${i}`}>
                      <td className="px-3 py-2 font-mono text-slate-500 whitespace-nowrap">
                        {(l.sent_at || "").replace("T", " ").slice(0, 19)}
                        {l.is_refire && (
                          <span className="ml-1.5 inline-block px-1 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700">
                            refire
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${netCls}`}>
                          {netLabel || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-slate-700">{l.action_id || "—"}</td>
                      <td className="px-3 py-2 font-mono text-slate-600">{l.status || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-mono font-semibold border ${respCls}`}>
                          {l.error_label || rs || "—"}
                        </span>
                        {l.error && !l.error_label && (
                          <span className="ml-1.5 text-[10px] text-red-500 italic" title={l.error}>
                            {l.error.length > 40 ? l.error.slice(0, 40) + "…" : l.error}
                          </span>
                        )}
                        {l.error_label && l.error && l.error !== l.error_label && (
                          <span className="ml-1.5 text-[10px] text-slate-500 italic" title={l.error}>
                            {l.error.length > 60 ? l.error.slice(0, 60) + "…" : l.error}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-500 max-w-[420px] truncate" title={l.postback_url || ""}>
                        {l.postback_url || "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isErr && l.action_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isRefiring}
                            onClick={() => refire(l)}
                            className="h-6 text-[10px] border-amber-200 text-amber-700 hover:bg-amber-50"
                            data-testid={`pub-pb-refire-${l.action_id}`}
                            title="Re-deliver this postback"
                          >
                            <ArrowsClockwise size={11} className={`mr-1 ${isRefiring ? "animate-spin" : ""}`} />
                            {isRefiring ? "Firing…" : "Refire"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
